import { type Compiler, type Diagnostics, type Factory, ObjectShape, type Shape } from '@servicenow/sdk-build-core'
import type { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import { resolveTableFieldNames } from '../utils/table-field-resolver'
import { type PillContext, resolvePlaybookPill } from '../utils/playbook-pill-resolver'
import { containsDataPill } from '../utils/playbook-shapes'
import { PARENT_RECORD_MAPPER_KEY } from '../constants/playbook-plugin-constants'

/**
 * Information about a declared playbook input — its mandatory flag is needed
 * so the parser can emit a build error when a required input is unmapped.
 */
export interface ValidInput {
    mandatory: boolean
}

/**
 * Result of parsing a mapper value — contains the serialized value and whether
 * it contains a data pill (determines sys_element_mapping vs sys_variable_value).
 */
export interface MapperValue {
    value: string
    isPill: boolean
}

interface ParseMapperOptions {
    mapperArrow: ArrowFunctionShape
    tableName: string
    validInputs: Map<string, ValidInput>
    compiler: Compiler
    rootDir: string
    diagnostics: Diagnostics
    factory: Factory
}

/**
 * Parse a mapper arrow `(trigger) => ({ key: wfa.playbook.dataPill(trigger.current.field) })` or
 * `() => ({ key: 'hardcoded value' })` into a Map<inputElementName, MapperValue>.
 *
 * Customers should write `(trigger) =>` when any mapped value pulls from the
 * trigger (e.g. `wfa.playbook.dataPill(trigger.current.priority)`) and
 * `() =>` when every mapped value is a hardcoded literal. The XML → Fluent
 * serializer mirrors this: `(trigger)` when any field is a pill, `()` when
 * every field is hardcoded.
 *
 * Returns entries like:
 *   "priority" → { value: "{{triggerRecord.priority}}", isPill: true }
 *   "record"   → { value: "{{triggerRecord}}", isPill: true }
 *   "summary"  → { value: "Incident {{triggerRecord.number}}: {{triggerRecord.short_description}}", isPill: true }
 *   "status"   → { value: "In Progress", isPill: false }
 *
 * Build-time diagnostics emitted:
 *  - Unknown key in the returned object (not a declared playbook input).
 *  - Field referenced via `trigger.current.<field>` does not exist on the
 *    trigger's table (skipped for unregistered tables — see resolveTableFieldNames).
 *  - A `mandatory: true` input was declared but is missing from the mapping.
 */
export async function parseMapperCallback(options: ParseMapperOptions): Promise<Map<string, MapperValue>> {
    const { mapperArrow, tableName, validInputs, compiler, rootDir, diagnostics, factory } = options
    const mappings = new Map<string, MapperValue>()
    const params = mapperArrow.getParameters()
    const triggerParamName = params[0]?.getName() ?? 'trigger'

    const tableFields = tableName ? resolveTableFieldNames(compiler, rootDir, tableName) : undefined

    const returnExpr = extractReturnedObject(mapperArrow)
    if (!returnExpr) {
        // Check for missing required inputs when no mapper object is returned
        for (const [inputName, info] of validInputs) {
            if (info.mandatory) {
                diagnostics.error(
                    mapperArrow,
                    `Required playbook input "${inputName}" is not mapped. The mapper must return an object.`
                )
            }
        }
        return mappings
    }

    // Create pill context for resolving dataPill() calls
    const pillContext: PillContext = {
        format: 'standard',
        factory,
        diagnostics,
        playbookBodyParamsNames: [],
        triggerParamName,
        allowedSources: {
            paramsInputs: false,
            paramsState: false,
            paramsParentRecord: false,
            previousActivityOutputs: false,
            currentActivityLabelDesc: false,
            automationPlan: false,
        },
    }

    for (const [key, value] of returnExpr.entries({ resolve: false })) {
        // parentRecord is a reserved key that maps to the playbook's auto-generated
        // parent_record process input. It is not a user-declared input but is always
        // accepted here; the trigger instance plugin handles it separately.
        if (key === PARENT_RECORD_MAPPER_KEY) {
            const isPill = containsDataPill(value)
            const resolved = await resolvePlaybookPill(value, pillContext)
            const serialized = resolved.ifString()?.getValue()
            if (serialized === undefined) {
                diagnostics.error(value, 'parentRecord must be a wfa.playbook.dataPill() call or a raw sysId string')
            } else if (isPill && !/^\{\{[^}]+\}\}$/.test(serialized)) {
                // parent_record is a reference field — it must resolve to a bare {{...}} pill or a plain
                // sys_id string. A template literal like `text ${dataPill(...)}` produces mixed content
                // which is not a valid reference value.
                diagnostics.error(
                    value,
                    'parentRecord must be a bare wfa.playbook.dataPill() call or a plain sys_id string. ' +
                        'Template literals with embedded pills are not supported.'
                )
            } else {
                mappings.set(PARENT_RECORD_MAPPER_KEY, { value: serialized, isPill })
            }
            continue
        }

        if (!validInputs.has(key)) {
            diagnostics.error(
                value,
                `"${key}" is not a declared playbook input. Declare it in the PlaybookDefinition inputs.`
            )
            continue
        }

        // Check if the value contains a data pill
        const isPill = containsDataPill(value)

        // Resolve the value (pills resolve to {{...}}, hardcoded values pass through)
        const resolved = await resolvePlaybookPill(value, pillContext)
        const serialized = resolved.ifString()?.getValue()

        if (serialized === undefined) {
            diagnostics.error(
                value,
                'Mapper values must be wfa.playbook.dataPill() calls, template literals, or hardcoded values'
            )
            continue
        }

        // Validate field exists on table (if table is registered and value is a pill)
        if (isPill && tableFields && serialized.startsWith('{{triggerRecord.')) {
            const fieldMatch = serialized.match(/triggerRecord\.([^}]+)/)
            if (fieldMatch) {
                const fieldPath = fieldMatch[1]
                const topLevelField = fieldPath.split('.')[0]
                if (topLevelField && !tableFields.has(topLevelField)) {
                    diagnostics.error(
                        value,
                        `Field "${topLevelField}" does not exist on table "${tableName}". ` +
                            `Check the spelling or declare the column on the table.`
                    )
                }
            }
        }

        mappings.set(key, { value: serialized, isPill })
    }

    // Coverage check: every mandatory input must be mapped
    for (const [inputName, info] of validInputs) {
        if (info.mandatory && !mappings.has(inputName)) {
            diagnostics.error(
                mapperArrow,
                `Required playbook input "${inputName}" is not mapped by the trigger's playbookInputs callback. ` +
                    `Map it (e.g. \`${inputName}: wfa.playbook.dataPill(trigger.current.<field>)\`) or remove \`mandatory: true\` from its declaration.`
            )
        }
    }

    return mappings
}

/**
 * Same coverage check as parseMapperCallback's tail, but for triggers that
 * don't pass a mapper callback at all. Returns the count of missing required
 * inputs so the caller can decide where to report the diagnostic.
 */
export function emitMissingRequiredInputDiagnostics(
    triggerCallSite: Shape,
    validInputs: Map<string, ValidInput>,
    diagnostics: Diagnostics
): void {
    for (const [inputName, info] of validInputs) {
        if (info.mandatory) {
            diagnostics.error(
                triggerCallSite,
                `Required playbook input "${inputName}" is not mapped. Add a playbookInputs callback to the trigger ` +
                    `(\`(trigger) => ({ ${inputName}: wfa.playbook.dataPill(trigger.current.<field>) })\`) ` +
                    `or remove \`mandatory: true\` from its declaration.`
            )
        }
    }
}

/**
 * Returns the object literal the arrow returns, whether via expression body
 * (`(trigger) => ({...})`) or a single `return {...}` statement in a block body.
 * Returns undefined if the arrow has no body, has a non-object return, or is
 * malformed in a way already caught by other diagnostics.
 */
function extractReturnedObject(mapperArrow: ArrowFunctionShape): ObjectShape | undefined {
    const directReturn = mapperArrow.getReturnValue()
    if (directReturn && directReturn.is(ObjectShape)) {
        return directReturn.as(ObjectShape)
    }
    // Block-bodied arrow with a single object return — accepted for ergonomics.
    const statements = mapperArrow.getStatements()
    if (statements.length === 1) {
        const only = statements[0]
        if (only && only.is(ObjectShape)) {
            return only.as(ObjectShape)
        }
    }
    return undefined
}
